// src/pages/ForgotPasswordComp.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import api from "../api/axios";
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { BrandLogo } from '../branding/BrandAsset';
import shapeIcon from "../assets/images/contact/4.png";

const ForgotPasswordComp = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 990px)');
  
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("info");
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    document.title = "Forgot Password | ProTradesInsight";
  }, []);

  const handleCloseAlert = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenAlert(false);
  };

  const showAlert = (message, severity = "info") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setOpenAlert(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!email) {
      showAlert("Please enter your email address.", "error");
      setLoading(false);
      return;
    }

    try {
      const response = await api.post('/auth/forgot-password', { email });
      
      if (response.data.success) {
        setEmailSent(true);
        showAlert(response.data.message || "Password reset link sent to your email!", "success");
      } else {
        showAlert(response.data.message || "Something went wrong. Please try again.", "error");
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      const message = error.response?.data?.message || "Failed to send reset link. Please try again.";
      showAlert(message, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="forgot-password-page">
      <Snackbar
        open={openAlert}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alertSeverity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {alertMessage}
        </Alert>
      </Snackbar>

      <div className="account padding-top padding-bottom sec-bg-color2" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
        <div className="container">
          <div className="account__wrapper" dataAos="fade-up" dataAosDuration="800">
            <div className="row g-4 justify-content-center">
              <div className="col-lg-6 col-md-8">
                <div className="account__content account__content--style1">
                  {/* Logo */}
                  <div className="text-center mb-4">
                    <BrandLogo alt="ProTradesInsight" style={{ height: 50, maxWidth: '100%', objectFit: 'contain' }} />
                  </div>

                  <div className="account__header text-center">
                    <h2>Forgot Password?</h2>
                    <p>
                      {emailSent 
                        ? "Check your email for the reset link." 
                        : "Enter your email address and we'll send you a link to reset your password."
                      }
                    </p>
                  </div>

                  {emailSent ? (
                    <div className="text-center">
                      <Alert severity="success" sx={{ mb: 4, borderRadius: 2 }}>
                        <strong>Email Sent!</strong>
                        <p className="mb-0 mt-1">
                          We've sent a password reset link to <strong>{email}</strong>.
                          Please check your inbox and spam folder.
                        </p>
                      </Alert>
                      
                      <div className="d-flex flex-column gap-3 mt-4">
                        <button
                          onClick={handleSubmit}
                          disabled={loading}
                          className="trk-btn trk-btn--border trk-btn--primary d-block w-100"
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Resending...
                            </>
                          ) : 'Resend Email'}
                        </button>
                        
                        <Link to="/login" className="text-center" style={{ color: '#000080', textDecoration: 'none', fontWeight: 500 }}>
                          ← Back to Login
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="account__form">
                      <div className="row g-4">
                        <div className="col-12">
                          <div>
                            <label htmlFor="email" className="form-label">Email Address</label>
                            <input
                              type="email"
                              className="form-control"
                              id="email"
                              placeholder="Enter your email"
                              required
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              autoComplete="email"
                            />
                          </div>
                        </div>
                      </div>

                      <button
                        type="submit"
                        className="trk-btn trk-btn--border trk-btn--primary d-block mt-4 w-100"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Sending...
                          </>
                        ) : 'Send Reset Link'}
                      </button>

                      <div className="text-center mt-3">
                        <Link to="/login" style={{ color: '#000080', textDecoration: 'none', fontWeight: 500 }}>
                          ← Back to Login
                        </Link>
                      </div>
                    </form>
                  )}

                  <div className="account__switch text-center mt-4">
                    <p style={{ color: '#666' }}>
                      Remember your password? <Link to="/login" style={{ color: '#000080', fontWeight: 600 }}>Login</Link>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="account__shape">
          <span className="account__shape-item account__shape-item--1">
            <img src={shapeIcon} alt="Shape icon" />
          </span>
        </div>
      </div>

      <style>
        {`
          .forgot-password-page {
            background-color: #f8f9fa;
            min-height: 100vh;
          }
          .account__content {
            background: #ffffff;
            padding: 40px 35px;
            border-radius: 16px;
            box-shadow: 0 10px 40px rgba(0,0,0,0.06);
          }
          .account__header h2 {
            color: #1a1a2e;
            font-weight: 700;
            font-size: 28px;
            margin-bottom: 10px;
          }
          .account__header p {
            color: #6a6a7a;
            font-size: 15px;
            line-height: 1.6;
          }
          .account__form .form-label {
            font-weight: 500;
            color: #333;
            margin-bottom: 6px;
          }
          .account__form .form-control {
            background-color: #ffffff !important;
            color: #000000 !important;
            border: 1px solid #ced4da !important;
            padding: 12px 16px;
            border-radius: 8px;
            transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
          }
          .account__form .form-control:focus {
            border-color: #000080 !important;
            box-shadow: 0 0 0 0.2rem rgba(0, 0, 128, 0.25) !important;
          }
          .trk-btn {
            padding: 12px 24px;
            border-radius: 8px;
            font-weight: 600;
            font-size: 16px;
            transition: all 0.3s ease;
            border: 2px solid transparent;
            cursor: pointer;
          }
          .trk-btn--primary {
            background-color: #000000 !important;
            color: #ffffff !important;
            border-color: #000000 !important;
          }
          .trk-btn--primary:hover {
            background-color: #000080 !important;
            color: #ffffff !important;
            border-color: #000080 !important;
            transform: translateY(-2px);
            box-shadow: 0 4px 15px rgba(0,0,128,0.3);
          }
          .trk-btn:disabled {
            opacity: 0.7;
            cursor: not-allowed;
          }
          @media (max-width: 768px) {
            .account__content {
              padding: 30px 20px;
            }
          }
        `}
      </style>
    </div>
  );
};

export default ForgotPasswordComp;
